import { RootState } from "../index";
import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { createAsset, destroyAsset, fetchAsset, fetchAssets, updateAsset } from "../../actions/roblox/assetsActions";

export interface AssetInterface {
  id: number;
  category_id: number;

  title: string;
  search_text: string;

  order: number;
  item_url: string;

  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  newParams: AssetInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null
};

const assetsAdapter = createEntityAdapter<AssetInterface>();

const slice = createSlice({
  name: 'assets',
  initialState: assetsAdapter.getInitialState(initialState),
  reducers: {
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchAssets.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchAssets.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.assets;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchAssets.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchAsset.fulfilled, (state, action) => {
        const asset = Object.values(action.payload.asset)[0];
        state.entities[asset.id] = asset;
      })

      // #create
      .addCase(createAsset.fulfilled, (state, action) => {
        const asset = Object.values(action.payload.asset)[0];
        state.entities[asset.id] = asset;
        state.ids.unshift(asset.id);
      })
      .addCase(createAsset.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateAsset.fulfilled, (state, action) => {
        const asset = Object.values(action.payload.asset)[0];
        state.entities[asset.id] = asset;
      })
      .addCase(updateAsset.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #destroy
      .addCase(destroyAsset.fulfilled, (state, action) => {
        assetsAdapter.removeOne(state, action.payload.id);
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setNewParams,
  setNewError,
  setEditId,
  setEditError
} = slice.actions;

export const {
  selectById: selectAssetById,
  selectIds: selectAssetIds,
  selectEntities: selectAssetEntities
} = assetsAdapter.getSelectors<RootState>(state => state.roblox.assets);
